import * as Sentry from '@sentry/sveltekit';
import { addExceptionMechanism } from '@sentry/utils';
const sentryUrlBlacklist = ['/autodiscover.xml', '/cdn-cgi/rum', '/xmlrpc.php', 'wordpress', '/wp/'];
export const SentryClientConfig = ({ dsn, release, environment, integrations, forceEnable = false, }) => {
    return {
        dsn: dsn,
        enabled: forceEnable || environment === 'production' || environment === 'prod',
        release: release,
        tracesSampleRate: 1.0,
        environment: environment,
        integrations: integrations,
        // Copied from https://docs.sentry.io/platforms/javascript/configuration/filtering/#decluttering-sentry
        ignoreErrors: [
            // Random plugins/extensions
            'top.GLOBALS',
            // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            'http://tt.epicplay.com',
            "Can't find variable: ZiteReader",
            'jigsaw is not defined',
            'ComboSearch is not defined',
            'http://loading.retry.widdit.com/',
            'atomicFindClose',
            // Facebook borked
            'fb_xd_fragment',
            // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
            // reduce this. (thanks @acdha)
            // See http://stackoverflow.com/questions/4113268
            'bmi_SafeAddOnload',
            'EBCallBackMessageReceived',
            // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
            'conduitPage',
            /beacon.min.js/,
        ],
        denyUrls: [
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
        ],
        beforeSendTransaction(event) {
            if (sentryUrlBlacklist.some(url => event.request?.url?.includes(url)))
                return null;
            return event;
        },
    };
};
export const SentryServerConfig = ({ dsn, release, environment, integrations, forceEnable = false, }) => {
    return {
        dsn: dsn,
        enabled: forceEnable ||
            ((environment === 'production' || environment === 'prod') && process?.env?.NODE_ENV !== 'development'),
        release: release,
        tracesSampleRate: 1.0,
        environment: environment,
        integrations: integrations,
    };
};
/**
 * Inspired by https://github.com/getsentry/sentry-javascript/blob/develop/packages/sveltekit/src/server/handleError.ts#L49
 *
 * When a page request fails because the page is not found, SvelteKit throws a "Not found" error.
 * In the error handler here, we can't access the response yet (which we do in the load instrumentation),
 * so we have to check if the error is a "Not found" error by checking if the route id is missing and
 * by checking the error message on top of the raw stack trace.
 */
export function isNotFoundError(input) {
    const { error, event } = input;
    const hasNoRouteId = !event.route || !event.route.id;
    const rawStack = (error != null &&
        typeof error === 'object' &&
        'stack' in error &&
        typeof error.stack === 'string' &&
        error.stack) ||
        '';
    return hasNoRouteId && rawStack.startsWith('Error: Not found:');
}
export function failedDynamicModuleImport(error) {
    // more info in https://woltair.atlassian.net/browse/WW-62 https://woltair.atlassian.net/browse/WW-278
    if ('localStorage' in window) {
        // this is there to prevent multiple reloads
        const forceReloadMessages = JSON.parse(String(localStorage.getItem('forceReloadAfterMessage'))) || [];
        if (!forceReloadMessages.includes(error.message) &&
            // firefox
            (error.message.includes('error loading dynamically imported module') ||
                // chrome
                error.message.includes('Failed to fetch dynamically imported module')) &&
            // this is there to reload only when "entry" or "nodes" files failed, because other errors should by handled by SvelteKit
            (error.message.includes('_app/immutable/entry') || error.message.includes('_app/immutable/nodes')) &&
            // this is there to prevent reload on error in external file
            error.message.includes(window.location.host)) {
            forceReloadMessages.push(error.message);
            localStorage.setItem('forceReloadAfterMessage', JSON.stringify(forceReloadMessages));
            return true;
        }
    }
    return false;
}
function defaultErrorHandler({ error }) {
    // eslint-disable-next-line no-console
    console.error(error);
}
function defaultBeforeCaptureException() { }
export function handleErrorWithSentry(beforeCaptureException = defaultBeforeCaptureException, handleError = defaultErrorHandler) {
    return (input) => {
        beforeCaptureException(input);
        Sentry.captureException(input.error, scope => {
            scope.addEventProcessor(event => {
                addExceptionMechanism(event, {
                    type: 'sveltekit',
                    handled: false,
                });
                return event;
            });
            return scope;
        });
        return handleError(input);
    };
}
